import type { ConsumerOffer } from '../types/dtos';
import { LanguageToggle } from './LanguageToggle';
import { TenantLogo } from './TenantLogo';
import { useT } from '../hooks/useT';

type Props = {
  offer: ConsumerOffer;
  selectedDeliveryDateId: string | null;
  selectedExtraIds: string[];
  dropOff: boolean;
};

export function ConfirmationView({ offer, selectedDeliveryDateId, selectedExtraIds, dropOff }: Props) {
  const { t, locale } = useT();
  const date = selectedDeliveryDateId
    ? offer.deliveryDates.find((d) => d.id === selectedDeliveryDateId)
    : undefined;
  const extras = offer.extraServicesOffered.filter((e) => selectedExtraIds.includes(e.id));

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <TenantLogo logoUrl={offer.branding?.logoUrl} />
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="h-6 w-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">{t('confirmation.title')}</h1>
        </div>
        <LanguageToggle compact />
      </div>

      {dropOff ? (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-medium text-slate-600">{t('confirmation.dropoff_title')}</div>
          <div className="mt-1 text-lg">{t('confirmation.dropoff_granted')}</div>
        </section>
      ) : (
        <>
          {date && (
            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-600">{t('confirmation.selected_date')}</div>
              <div className="mt-1 text-lg">
                {formatDate(date.dateValue, locale)}
                {date.windowStart && date.windowEnd && (
                  <span className="ml-2 text-slate-600">
                    {formatTime(date.windowStart, locale)}–{formatTime(date.windowEnd, locale)}
                  </span>
                )}
                {date.displayLabel && !date.windowStart && (
                  <span className="ml-2 text-slate-600">{date.displayLabel}</span>
                )}
              </div>
            </section>
          )}

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-sm font-medium text-slate-600">{t('confirmation.selected_extras')}</div>
            {extras.length === 0 ? (
              <div className="mt-1 text-slate-500">{t('confirmation.no_extras')}</div>
            ) : (
              <ul className="mt-1 list-inside list-disc">
                {extras.map((e) => (
                  <li key={e.id}>{e.label}</li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function formatDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(`${iso}T00:00:00`));
}
function formatTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}
